import { nothing } from 'lit'
import { AsyncDirective } from 'lit/async-directive.js'
import { directive, type ElementPart, type PartInfo, PartType } from 'lit/directive.js'
import {
	catchError,
	EMPTY,
	filter,
	finalize,
	fromEvent,
	merge,
	Observable,
	ReplaySubject,
	Subject,
	switchMap,
	take,
	takeUntil,
	tap,
	timer,
} from 'rxjs'
import { absorb } from './absorb'
import { hideDropZone, showDropZone } from './drop-indicator'
import { pointerSession } from './pointer-session'
import { reducedMotion$ } from './reduced-motion'
import { capturePreflightStyle, createLayoutSeat } from './seat'

export interface CarryLabels {
	item: (name: string) => string
	picked: (name: string, total: number) => string
	offered: (name: string, destination: string, position: number, total: number) => string
	empty: (name: string) => string
	dropped: (name: string, destination: string) => string
	cancelled: (name: string) => string
}

export interface CarryOptions {
	disabled?: boolean
	labels?: Partial<CarryLabels>
}

export interface IntoOptions {
	disabled?: boolean
	accepts?: (sourceId: string) => boolean
}

export type SchmancyIntoEvent = CustomEvent<{
	/** The carried element's id. */
	source: string
	/** The id of the `into` element it landed in. */
	destination: string
	via: 'pointer' | 'keyboard'
	/**
	 * The flight, still in the air — the event fires while it runs, not after
	 * it. A consumer whose move is refused unsubscribes and the node returns to
	 * its place, instead of having already been shown as swallowed. It completes
	 * without emitting when the flight was reversed or the DOM took it away.
	 */
	absorbed: Observable<void>
}>

const DEFAULT_LABELS: CarryLabels = {
	item: name => `Carry ${name}`,
	picked: (name, total) =>
		`${name} picked up, ${total} place${total === 1 ? '' : 's'} to put it. Use the arrow keys or type a name to choose one, enter to move, escape to cancel.`,
	offered: (_name, destination, position, total) => `${destination}, ${position} of ${total}.`,
	empty: name => `${name} picked up. Nothing here can hold it.`,
	dropped: (name, destination) => `${name} moved into ${destination}.`,
	cancelled: name => `${name} put back.`,
}

const SWALLOW_CLICK_MS = 300
const SWALLOW_CLICK_SLOP_PX = 8
const PREFIX_RESET_MS = 700

const HANDLE_ATTRIBUTES = ['role', 'tabindex', 'aria-disabled', 'aria-label', 'aria-keyshortcuts'] as const
/** `explicitHandle`'s own accessibility surface — no `tabindex`/`aria-keyshortcuts`: no keyboard path ever reaches it. */
const EXPLICIT_HANDLE_ATTRIBUTES = ['role', 'aria-label'] as const

interface CarryRecord {
	id: string
	disabled: boolean
	labels: CarryLabels
	destroy$: Subject<void>
	handle?: HTMLElement
	handleAttributes?: Map<(typeof HANDLE_ATTRIBUTES)[number], string | null>
	handleStyle?: {
		attribute: string | null
		cursor: string
		userSelect: string
	}
	/** The touch/pen-only handle currently governed (an authored `data-carry-handle`) — `undefined` when none is authored. */
	explicitHandle?: HTMLElement
	explicitHandleAttributes?: Map<(typeof EXPLICIT_HANDLE_ATTRIBUTES)[number], string | null>
	/** `explicitHandle`'s own `touch-action` before `dress()` forced it to `'none'`, restored by `restoreExplicitHandle()`. */
	explicitHandleTouchAction?: string
}

interface IntoRecord {
	id: string
	disabled: boolean
	accepts?: (sourceId: string) => boolean
}

/**
 * Set only for a pointer carry whose pickup successfully lifted the element
 * onto the popover top layer (see `pick()`). `null` for every keyboard carry
 * and for a pointer carry where the platform has no Popover API or the
 * promotion was refused — those stay in place, dimming and shrinking exactly
 * as before, with no position tracking.
 */
interface CarryPromotion {
	seat: HTMLElement
	previousPopover: string | null
	/** The resting rect's own top-left, read at pickup — the `left`/`top` base every `moved` step offsets from. */
	originLeft: number
	originTop: number
}

interface CarrySession {
	id: string
	element: HTMLElement
	via: 'pointer' | 'keyboard'
	offered: HTMLElement | null
	restingStyle: string | null
	restingCarrying: string | null
	prefix: string
	prefixAt: number
	promotion: CarryPromotion | null
}

interface FlightRecord {
	destination: HTMLElement
	sourceId: string
	cancel$: Subject<void>
}

const carriers = new WeakMap<HTMLElement, CarryRecord>()
const receivers = new WeakMap<HTMLElement, IntoRecord>()

/**
 * Every live `into` element, in one flat set. NOT bucketed by root node at
 * registration time: lit runs an element-part directive on the cloned template
 * fragment and inserts it afterwards, so `getRootNode()` there is that fragment
 * and nothing ever looks a destination up under it — the pair would be dead on
 * first paint and come alive only once a re-render happened to re-key it.
 * `candidates()` compares roots at gesture time instead, where both ends are in
 * the tree they will actually be carried in.
 */
const destinations = new Set<HTMLElement>()
const flights = new Map<HTMLElement, FlightRecord>()
const inFlight = new WeakSet<HTMLElement>()

let session: CarrySession | null = null
let liveRegion: HTMLElement | null = null

function findInOpenTree(root: ParentNode, accepts: (element: HTMLElement) => boolean): HTMLElement | undefined {
	if (root instanceof HTMLElement && root.shadowRoot) {
		const shadowMatch = findInOpenTree(root.shadowRoot, accepts)
		if (shadowMatch) return shadowMatch
	}
	for (const child of root.children) {
		if (!(child instanceof HTMLElement)) continue
		if (accepts(child)) return child
		if (child instanceof HTMLSlotElement) {
			for (const assigned of child.assignedElements({ flatten: true })) {
				if (!(assigned instanceof HTMLElement)) continue
				if (accepts(assigned)) return assigned
				const assignedMatch = findInOpenTree(assigned, accepts)
				if (assignedMatch) return assignedMatch
			}
		}
		const lightMatch = findInOpenTree(child, accepts)
		if (lightMatch) return lightMatch
	}
	return undefined
}

function isEnabledFocusable(element: HTMLElement): boolean {
	return (
		element.matches('button, a[href], input, select, textarea, [tabindex], [contenteditable="true"]') &&
		!element.matches('[disabled], [aria-disabled="true"], [tabindex="-1"]')
	)
}

function nameFor(element: HTMLElement): string {
	const direct = element.getAttribute('aria-label')?.trim()
	if (direct) return direct

	const root = element.getRootNode()
	const labelled = element
		.getAttribute('aria-labelledby')
		?.split(/\s+/)
		.map(id => (root instanceof Document || root instanceof ShadowRoot ? root.getElementById(id)?.textContent?.trim() : ''))
		.filter(Boolean)
		.join(' ')
	if (labelled) return labelled

	const focusable = findInOpenTree(element, isEnabledFocusable)
	const nested = focusable?.getAttribute('aria-label')?.trim() ?? focusable?.textContent?.trim()
	return nested || element.textContent?.trim() || 'item'
}

function composedContains(ancestor: Node, descendant: Node): boolean {
	let current: Node | null = descendant
	while (current) {
		if (current === ancestor) return true
		if (current instanceof Element && current.assignedSlot) {
			current = current.assignedSlot
			continue
		}
		if (current.parentNode) {
			current = current.parentNode
			continue
		}
		const root = current.getRootNode()
		current = root instanceof ShadowRoot ? root.host : null
	}
	return false
}

function ensureLiveRegion(): HTMLElement {
	if (liveRegion?.isConnected) return liveRegion
	const region = document.createElement('div')
	region.setAttribute('role', 'status')
	region.setAttribute('aria-live', 'polite')
	Object.assign(region.style, {
		position: 'fixed',
		width: '1px',
		height: '1px',
		margin: '-1px',
		padding: '0',
		border: '0',
		overflow: 'hidden',
		clipPath: 'inset(50%)',
		whiteSpace: 'nowrap',
	})
	document.body.appendChild(region)
	liveRegion = region
	return region
}

function announce(message: string): void {
	const region = ensureLiveRegion()
	region.textContent = region.textContent === message ? `${message} ` : message
}

function restoreHandle(record: CarryRecord): void {
	if (!record.handle || !record.handleAttributes || !record.handleStyle) return
	for (const name of HANDLE_ATTRIBUTES) {
		const value = record.handleAttributes.get(name)
		if (value === null) record.handle.removeAttribute(name)
		else if (value !== undefined) record.handle.setAttribute(name, value)
	}
	if (record.handleStyle.attribute === null) record.handle.removeAttribute('style')
	else record.handle.setAttribute('style', record.handleStyle.attribute)
	record.handle = undefined
	record.handleAttributes = undefined
	record.handleStyle = undefined
}

/** `restoreHandle()`'s counterpart for the touch/pen-only handle. */
function restoreExplicitHandle(record: CarryRecord): void {
	if (record.explicitHandle) {
		for (const name of EXPLICIT_HANDLE_ATTRIBUTES) {
			const value = record.explicitHandleAttributes?.get(name)
			if (value === null) record.explicitHandle.removeAttribute(name)
			else if (value !== undefined) record.explicitHandle.setAttribute(name, value)
		}
		record.explicitHandle.style.touchAction = record.explicitHandleTouchAction ?? ''
	}
	record.explicitHandle = undefined
	record.explicitHandleAttributes = undefined
	record.explicitHandleTouchAction = undefined
}

function candidates(carried: CarrySession): HTMLElement[] {
	const root = carried.element.getRootNode()
	return [...destinations]
		.filter(destination => {
			if (destination === carried.element || !destination.isConnected) return false
			// Scoped to the source's own root, so a destination mounted in an
			// unrelated view is never a candidate.
			if (destination.getRootNode() !== root) return false
			const receiver = receivers.get(destination)
			return receiver !== undefined && !receiver.disabled && (receiver.accepts?.(carried.id) ?? true)
		})
		.sort((a, b) => {
			if (a === b) return 0
			return a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1
		})
}

function offer(carried: CarrySession, destination: HTMLElement | null): void {
	carried.offered = destination
	if (destination) showDropZone(destination)
	else hideDropZone()
}

function offerAt(carried: CarrySession, x: number, y: number): void {
	const root = carried.element.getRootNode()
	const reachable = candidates(carried)
	if (!(root instanceof Document || root instanceof ShadowRoot) || reachable.length === 0) return offer(carried, null)

	if (root instanceof ShadowRoot) {
		const pageTop = carried.element.ownerDocument.elementsFromPoint(x, y)[0]
		if (pageTop && !composedContains(root.host, pageTop) && !composedContains(pageTop, root.host)) {
			return offer(carried, null)
		}
	}

	const painted = root.elementsFromPoint(x, y)[0]
	if (!painted) return offer(carried, null)
	offer(carried, reachable.find(destination => composedContains(destination, painted)) ?? null)
}

function pick(element: HTMLElement, record: CarryRecord, via: 'pointer' | 'keyboard'): CarrySession {
	const rect = element.getBoundingClientRect()
	const opened: CarrySession = {
		id: record.id,
		element,
		via,
		offered: null,
		restingStyle: element.getAttribute('style'),
		restingCarrying: element.getAttribute('data-carry-carrying'),
		prefix: '',
		prefixAt: 0,
		promotion: null,
	}
	session = opened
	element.setAttribute('data-carry-carrying', '')
	element.style.opacity = '0.6'
	if (!reducedMotion$.value) element.style.transition = 'opacity 120ms ease, transform 160ms ease'

	// Lift the element onto the popover top layer, pinned at its resting rect
	// via `position: fixed`, so the `left`/`top` writes `listen()` makes on
	// every `moved` step below escape every ancestor's clipping/scroll region
	// instead of being clamped to it — the same technique `absorb()` uses for
	// the post-drop flight (a same-box seat holds the layout, the popover API
	// supplies the escape), applied here at pickup instead of at landing.
	// Keyboard has no pointer position to follow, so this lift is
	// pointer-only; it also declines silently wherever the escape isn't
	// available — no Popover API, no parent, already an open popover, or
	// `showPopover()` itself throws — leaving `element` exactly as the
	// mutations above already have it: dimming and shrinking in place, the
	// correct fallback.
	const canPromote =
		via === 'pointer' && typeof element.showPopover === 'function' && typeof element.hidePopover === 'function' && !!element.parentNode
	let alreadyPromoted = true
	if (canPromote) {
		try {
			alreadyPromoted = element.matches(':popover-open')
		} catch {
			alreadyPromoted = true // can't tell — the same refusal as genuinely already open
		}
	}
	if (canPromote && !alreadyPromoted) {
		const entryStyle = element.getAttribute('style')
		const previousPopover = element.getAttribute('popover')
		const preflight = capturePreflightStyle(element)
		const seat = createLayoutSeat(rect, preflight, 'data-schmancy-carry-seat')

		element.before(seat)
		Object.assign(element.style, {
			position: 'fixed',
			inset: 'auto',
			left: `${rect.left}px`,
			top: `${rect.top}px`,
			boxSizing: 'border-box',
			width: `${rect.width}px`,
			height: `${rect.height}px`,
			maxWidth: 'none',
			maxHeight: 'none',
			margin: '0',
			padding: preflight.padding,
			border: preflight.border,
			background: preflight.background,
			color: preflight.color,
			display: preflight.display,
			overflow: preflight.overflow,
			// Hit-tested out: `offerAt()`'s `elementsFromPoint()` must see
			// through this element to whatever destination sits under the
			// pointer, since this element now sits at the pointer's own
			// position throughout the drag — without this every drop would
			// offer only itself.
			pointerEvents: 'none',
			willChange: 'left, top, transform',
		})

		try {
			element.setAttribute('popover', 'manual')
			element.showPopover()
			if (!element.matches(':popover-open')) throw new Error('popover promotion was refused')
			opened.promotion = { seat, previousPopover, originLeft: rect.left, originTop: rect.top }
		} catch {
			seat.remove()
			if (previousPopover === null) element.removeAttribute('popover')
			else element.setAttribute('popover', previousPopover)
			if (entryStyle === null) element.removeAttribute('style')
			else element.setAttribute('style', entryStyle)
		}
	}

	return opened
}

/**
 * The one arbiter every session-ending path already calls — `land()` (both
 * before a successful absorb and on no-destination-or-refused), `discarded`,
 * `Escape`, `CarryDirective.update()`'s id-change/disabled branch, and
 * `disconnected()` — so a promotion `pick()` opened is torn down uniformly
 * everywhere a session can end, with no separate call needed at any of those
 * sites. The style restore below (from `restingStyle`, snapshotted before
 * `pick()` touched anything) wipes the `position: fixed` etc. `pick()` may
 * have set; this only has to undo what a plain style-attribute replace
 * cannot: the seat element and the popover promotion itself. Landing into
 * `absorb()` therefore always hands it a clean, unpromoted element, exactly
 * as it received one before this file tracked the pointer at all — absorb's
 * own `wasPopoverOpen` bookkeeping never sees a promotion it didn't open.
 */
function close(carried: CarrySession): void {
	hideDropZone()
	const promotion = carried.promotion
	if (promotion) {
		promotion.seat.remove()
		try {
			if (carried.element.matches(':popover-open')) carried.element.hidePopover()
		} catch {
			// Disconnected, or already closed — nothing left to undo.
		}
		if (promotion.previousPopover === null) carried.element.removeAttribute('popover')
		else carried.element.setAttribute('popover', promotion.previousPopover)
		carried.promotion = null
	}
	if (carried.restingCarrying === null) carried.element.removeAttribute('data-carry-carrying')
	else carried.element.setAttribute('data-carry-carrying', carried.restingCarrying)
	if (carried.restingStyle === null) carried.element.removeAttribute('style')
	else carried.element.setAttribute('style', carried.restingStyle)
	if (session === carried) session = null
}

function cancelFlight(source: HTMLElement): void {
	const flight = flights.get(source)
	if (!flight) return
	flight.cancel$.next()
}

/**
 * Release over the offered destination: start the flight and say so in the same
 * tick, so a move that is refused after the fact can still be reversed —
 * an animation that has already said "swallowed" cannot be taken back.
 * Released anywhere else the session simply closes with no event, as does a
 * `pointercancel`: a gesture the OS took away expressed no intent to drop.
 */
function land(carried: CarrySession, labels: CarryLabels): void {
	const destination = carried.offered
	const receiver = destination ? receivers.get(destination) : undefined
	const sourceName = nameFor(carried.element)
	const destinationName = destination ? nameFor(destination) : ''
	close(carried)
	if (
		!destination ||
		!receiver ||
		receiver.disabled ||
		!(receiver.accepts?.(carried.id) ?? true)
	) {
		return
	}

	const cancel$ = new Subject<void>()
	const flight: FlightRecord = { destination, sourceId: carried.id, cancel$ }
	// ReplaySubject, not Subject: under reduced motion `absorb()` emits and
	// completes synchronously, before the event below is even dispatched, and a
	// consumer subscribing from the handler would see a flight that never spoke.
	const landed$ = new ReplaySubject<void>(1)
	let settled = false
	inFlight.add(carried.element)
	flights.set(carried.element, flight)
	absorb(carried.element, destination)
		.pipe(
			takeUntil(cancel$),
			tap(() => {
				// Over the moment it lands: from here `absorbed` has nothing left to
				// reverse, whatever a consumer unsubscribing afterwards intended.
				settled = true
				if (!carried.element.isConnected || !destination.isConnected) return
				if (carried.via === 'keyboard') (findInOpenTree(destination, isEnabledFocusable) ?? destination).focus()
				announce(labels.dropped(sourceName, destinationName))
			}),
			// The DOM taking the animation away — the consumer's own re-render
			// removing the node it just accepted — is the ordinary end of a flight,
			// not a fault to raise on a consumer who subscribed to hold it.
			catchError(() => EMPTY),
			finalize(() => {
				settled = true
				inFlight.delete(carried.element)
				if (flights.get(carried.element) === flight) flights.delete(carried.element)
				cancel$.complete()
			}),
		)
		.subscribe(landed$)

	destination.dispatchEvent(
		new CustomEvent('into', {
			bubbles: true,
			composed: true,
			detail: {
				source: carried.id,
				destination: receiver.id,
				via: carried.via,
				absorbed: new Observable<void>(subscriber => {
					const held = landed$.subscribe(subscriber)
					return () => {
						held.unsubscribe()
						if (!settled) cancel$.next()
					}
				}),
			},
		}),
	)
}

class CarryDirective extends AsyncDirective {
	private element?: HTMLElement

	constructor(partInfo: PartInfo) {
		super(partInfo)
		if (partInfo.type !== PartType.ELEMENT) throw new Error('carry directive can only be used on elements')
	}

	render(id: string, options?: CarryOptions) {
		void id
		void options
		return nothing
	}

	update(part: ElementPart, [id, options = {}]: [string, CarryOptions?]) {
		if (!(part.element instanceof HTMLElement)) throw new Error('carry directive can only be used on HTML elements')
		this.element = part.element
		const existing = carriers.get(part.element)
		if (existing) {
			const disabled = options.disabled ?? false
			if (existing.id !== id || (!existing.disabled && disabled)) {
				const carried = session
				if (carried?.element === part.element) close(carried)
				cancelFlight(part.element)
			}
			existing.id = id
			existing.disabled = disabled
			existing.labels = { ...DEFAULT_LABELS, ...options.labels }
			this.dress(part.element, existing)
			return nothing
		}

		const record: CarryRecord = {
			id,
			disabled: options.disabled ?? false,
			labels: { ...DEFAULT_LABELS, ...options.labels },
			destroy$: new Subject<void>(),
		}
		carriers.set(part.element, record)
		this.dress(part.element, record)
		this.listen(part.element, record)
		return nothing
	}

	private dress(element: HTMLElement, record: CarryRecord): void {
		// Mouse and keyboard: the first enabled-focusable descendant, else the
		// whole element. `data-carry-handle` is deliberately NEVER checked here:
		// checking it would silently narrow what a mouse can grab to whatever
		// `data-carry-handle` marks the moment one is authored for touch's
		// sake — the exact "I can't drag the file with my mouse" bug this whole
		// mechanism exists to fix. See the touch/pen block below for the one
		// place that DOES read `data-carry-handle` — the two resolutions stay
		// fully independent.
		const handle = findInOpenTree(element, isEnabledFocusable) ?? element
		if (record.handle !== handle) {
			const carried = session
			if (carried?.element === element) close(carried)
			cancelFlight(element)
			restoreHandle(record)
			record.handle = handle
			record.handleAttributes = new Map([
				...HANDLE_ATTRIBUTES.map(name => [name, handle.getAttribute(name)] as const),
			])
			record.handleStyle = {
				attribute: handle.getAttribute('style'),
				cursor: handle.style.cursor,
				userSelect: handle.style.userSelect,
			}
		}

		const ownsRole =
			record.handleAttributes?.get('role') === null &&
			!handle.matches('button, a[href], input, select, textarea')
		if (ownsRole) {
			handle.setAttribute('role', 'button')
		}
		if (!handle.hasAttribute('tabindex') && handle.tabIndex < 0) handle.tabIndex = 0
		if (ownsRole) handle.setAttribute('aria-disabled', String(record.disabled))
		else {
			const authoredDisabled = record.handleAttributes?.get('aria-disabled')
			if (authoredDisabled === null) handle.removeAttribute('aria-disabled')
			else if (authoredDisabled !== undefined) handle.setAttribute('aria-disabled', authoredDisabled)
		}
		const shortcuts = record.handleAttributes?.get('aria-keyshortcuts')?.split(/\s+/).filter(Boolean) ?? []
		if (!record.disabled && !shortcuts.includes('Alt+M')) shortcuts.push('Alt+M')
		if (shortcuts.length === 0) handle.removeAttribute('aria-keyshortcuts')
		else handle.setAttribute('aria-keyshortcuts', shortcuts.join(' '))
		if (record.disabled) {
			handle.style.cursor = record.handleStyle?.cursor ?? ''
			handle.style.userSelect = record.handleStyle?.userSelect ?? ''
		} else {
			handle.style.cursor = 'grab'
			handle.style.userSelect = 'none'
		}

		// Touch and pen: the authored `data-carry-handle` alone, or nothing — the
		// ONLY place in this method `data-carry-handle` is read. A wholly separate
		// element from `handle` above whenever one is authored, so it gets its own
		// small accessibility surface (a role, a name) rather than borrowing
		// `handle`'s: no `tabindex`/`aria-keyshortcuts` here, because neither
		// keyboard nor Alt+M ever reaches this element (`keyboard$` below gates on
		// `handle` alone) — offering either would be a dead end. `touch-action:
		// none` also lives here alone, never on `handle`: forcing it onto a
		// fallback-resolved element touch can never pick up would swallow that
		// element's native scroll for nothing — exactly the regression this split
		// exists to avoid.
		const explicitHandle = element.hasAttribute('data-carry-handle')
			? element
			: findInOpenTree(element, candidate => candidate.hasAttribute('data-carry-handle'))
		if (record.explicitHandle !== explicitHandle) {
			restoreExplicitHandle(record)
			record.explicitHandle = explicitHandle
			record.explicitHandleAttributes = explicitHandle
				? new Map(EXPLICIT_HANDLE_ATTRIBUTES.map(name => [name, explicitHandle.getAttribute(name)] as const))
				: undefined
			record.explicitHandleTouchAction = explicitHandle?.style.touchAction
		}
		if (explicitHandle) {
			if (
				record.explicitHandleAttributes?.get('role') === null &&
				!explicitHandle.matches('button, a[href], input, select, textarea')
			) {
				explicitHandle.setAttribute('role', 'button')
			}
			if (!explicitHandle.hasAttribute('aria-label') && !explicitHandle.hasAttribute('aria-labelledby')) {
				const subject = nameFor(element)
				if (subject !== 'item') explicitHandle.setAttribute('aria-label', record.labels.item(subject))
			}
			explicitHandle.style.touchAction = record.disabled ? (record.explicitHandleTouchAction ?? '') : 'none'
		}

		ensureLiveRegion()
	}

	private listen(element: HTMLElement, record: CarryRecord): void {
		const swallowClick$ = new Subject<{ x: number; y: number }>()
		// Where the pointer opened the session — the live scale below shrinks with
		// distance from here. Pointer-only: a keyboard carry never sets it, and its
		// `tap` below never reads it.
		let start: { x: number; y: number } | null = null

		const pointer$ = pointerSession(element, {
			enabled: () => !record.disabled && session === null && !inFlight.has(element),
			starts: down => {
				this.dress(element, record)
				// Mouse alone gets the fallback handle (`record.handle` — see
				// `dress()`'s own comment for why it never checks `data-carry-handle`);
				// touch and pen only ever start from an authored `data-carry-handle`
				// (`record.explicitHandle`), matching `reorder()`'s own precedent of
				// never falling back for a handle-less touch drag — so a touch press
				// anywhere else on the tile is not a carry at all, and its native
				// scroll survives untouched.
				const handle = down.pointerType === 'mouse' ? record.handle : record.explicitHandle
				return handle !== undefined && down.composedPath().includes(handle)
			},
		}).pipe(
			tap(step => {
				if (step.phase === 'opened') {
					pick(element, record, 'pointer')
					start = { x: step.event.clientX, y: step.event.clientY }
				}
				const carried = session
				if (!carried || carried.element !== element) return
				if (step.phase === 'discarded') {
					start = null
					return close(carried)
				}
				offerAt(carried, step.event.clientX, step.event.clientY)
				if (start) {
					// Position and scale live on separate properties so neither clobbers
					// the other in one assignment. `left`/`top` carry the pointer-follow —
					// written with no transition (see pick()'s `transition` list), so the
					// element snaps to the pointer every step rather than lagging behind
					// an eased one; a `position: fixed` element's own left/top is an
					// isolated, cheap layout recompute; it doesn't ripple through the
					// grid the seat is holding its place in. `transform: scale(...)` below
					// keeps its existing eased transition, because unlike the follow —
					// without which the element simply does not move — the shrink is pure
					// decoration: it tracks regardless of reducedMotion$, the shrink does not.
					if (carried.promotion) {
						element.style.left = `${carried.promotion.originLeft + (step.event.clientX - start.x)}px`
						element.style.top = `${carried.promotion.originTop + (step.event.clientY - start.y)}px`
					}
					// Ambient shrink converges on 0.88 as the pointer travels from pickup;
					// hovering a valid destination overrides it with a deeper, fixed
					// "about to be absorbed" preview ahead of the actual absorb() flight.
					// The ambient term is a continuous per-frame effect and drops out
					// entirely under reducedMotion$ (mirroring absorb()'s own full bail);
					// the offer cue is a discrete state change, so — like the opacity dim
					// in pick() and showDropZone() below — its VALUE still applies under
					// reduced motion, and only pick()'s transition is what stops animating.
					const scale = carried.offered
						? 0.7
						: reducedMotion$.value
							? 1
							: 1 - Math.min(Math.hypot(step.event.clientX - start.x, step.event.clientY - start.y) / 400, 1) * 0.12
					element.style.transform = `scale(${scale})`
				}
				if (step.phase !== 'committed') return
				start = null
				swallowClick$.next({ x: step.event.clientX, y: step.event.clientY })
				land(carried, record.labels)
			}),
		)

		const click$ = swallowClick$.pipe(
			switchMap(released =>
				fromEvent<MouseEvent>(window, 'click', { capture: true }).pipe(
					filter(
						click =>
							click.composedPath().includes(element) &&
							Math.hypot(click.clientX - released.x, click.clientY - released.y) <= SWALLOW_CLICK_SLOP_PX,
					),
					take(1),
					takeUntil(timer(SWALLOW_CLICK_MS)),
				),
			),
			tap(click => {
				click.preventDefault()
				click.stopPropagation()
			}),
		)

		const keyboard$ = fromEvent<KeyboardEvent>(element, 'keydown').pipe(
			filter(stroke => {
				this.dress(element, record)
				return (
					record.handle !== undefined &&
					stroke.composedPath().includes(record.handle) &&
					!record.disabled &&
					!inFlight.has(element)
				)
			}),
			tap(stroke => {
				const carried = session?.element === element ? session : null
				const name = nameFor(element)

				if (stroke.altKey && stroke.code === 'KeyM') {
					stroke.preventDefault()
					if (carried || session) return
					const opened = pick(element, record, 'keyboard')
					const reachable = candidates(opened)
					offer(opened, reachable[0] ?? null)
					announce(reachable.length === 0 ? record.labels.empty(name) : record.labels.picked(name, reachable.length))
					return
				}

				if (!carried) return

				if (stroke.key === 'Escape') {
					stroke.preventDefault()
					close(carried)
					;(record.handle ?? element).focus()
					announce(record.labels.cancelled(name))
					return
				}

				if (stroke.key === 'Enter' || stroke.key === ' ' || stroke.key === 'Spacebar') {
					stroke.preventDefault()
					land(carried, record.labels)
					return
				}

				const step =
					stroke.key === 'ArrowRight' || stroke.key === 'ArrowDown'
						? 1
						: stroke.key === 'ArrowLeft' || stroke.key === 'ArrowUp'
							? -1
							: 0
				if (step !== 0) {
					stroke.preventDefault()
					const reachable = candidates(carried)
					if (reachable.length === 0) return
					const at = carried.offered ? reachable.indexOf(carried.offered) : -1
					const next = reachable[(((at + step) % reachable.length) + reachable.length) % reachable.length]
					if (!next) return
					offer(carried, next)
					announce(record.labels.offered(name, nameFor(next), reachable.indexOf(next) + 1, reachable.length))
					return
				}

				if (stroke.altKey || stroke.ctrlKey || stroke.metaKey || stroke.key.length !== 1 || /\s/.test(stroke.key)) return
				stroke.preventDefault()
				const now = performance.now()
				carried.prefix = now - carried.prefixAt > PREFIX_RESET_MS ? stroke.key.toLocaleLowerCase() : carried.prefix + stroke.key.toLocaleLowerCase()
				carried.prefixAt = now
				const reachable = candidates(carried)
				const matched = reachable.find(destination => nameFor(destination).toLocaleLowerCase().startsWith(carried.prefix))
				if (!matched) return
				offer(carried, matched)
				announce(record.labels.offered(name, nameFor(matched), reachable.indexOf(matched) + 1, reachable.length))
			}),
		)

		const dressed$ = timer(0).pipe(tap(() => this.dress(element, record)))
		merge(pointer$, click$, keyboard$, dressed$).pipe(takeUntil(record.destroy$)).subscribe()
	}

	override disconnected() {
		const element = this.element
		const record = element ? carriers.get(element) : undefined
		if (!element || !record) return
		const carried = session
		if (carried?.element === element) close(carried)
		cancelFlight(element)
		record.destroy$.next()
		record.destroy$.complete()
		restoreHandle(record)
		restoreExplicitHandle(record)
	}

	override reconnected() {
		const element = this.element
		const record = element ? carriers.get(element) : undefined
		if (!element || !record) return
		record.destroy$ = new Subject<void>()
		this.dress(element, record)
		this.listen(element, record)
	}
}

class IntoDirective extends AsyncDirective {
	private element?: HTMLElement

	constructor(partInfo: PartInfo) {
		super(partInfo)
		if (partInfo.type !== PartType.ELEMENT) throw new Error('into directive can only be used on elements')
	}

	render(id: string, options?: IntoOptions) {
		void id
		void options
		return nothing
	}

	update(part: ElementPart, [id, options = {}]: [string, IntoOptions?]) {
		if (!(part.element instanceof HTMLElement)) throw new Error('into directive can only be used on HTML elements')
		if (this.element && this.element !== part.element) this.unregister()
		this.element = part.element
		const previous = receivers.get(part.element)
		const receiver: IntoRecord = { id, disabled: options.disabled ?? false, accepts: options.accepts }
		receivers.set(part.element, receiver)
		destinations.add(part.element)
		const carried = session
		if (carried?.offered === part.element) {
			if (previous && previous.id !== id) close(carried)
			else if (receiver.disabled || !(receiver.accepts?.(carried.id) ?? true)) offer(carried, null)
		}
		for (const [source, flight] of flights) {
			if (
				flight.destination === part.element &&
				(previous?.id !== id || receiver.disabled || !(receiver.accepts?.(flight.sourceId) ?? true))
			) {
				cancelFlight(source)
			}
		}
		return nothing
	}

	private unregister(): void {
		if (!this.element) return
		// A detached destination is already refused by `candidates()`; this is what
		// keeps the registry from holding the node itself alive for the page's life.
		destinations.delete(this.element)
		const carried = session
		if (carried?.offered === this.element) offer(carried, null)
		for (const [source, flight] of flights) if (flight.destination === this.element) cancelFlight(source)
	}

	override disconnected() {
		this.unregister()
	}

	override reconnected() {
		if (this.element) destinations.add(this.element)
	}
}

export const carry = directive(CarryDirective)
export const into = directive(IntoDirective)
